import React, { useState, useEffect, useRef } from 'react';
import { Card, Col, Form, Row, Spinner, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import type { MatchedProductsFilters } from '../../../../apps/hooks/sourcing/useMatchedProducts';
import { ROUTES } from '../../../../routes/routes';
import { ScrapingStatus } from '../../../../models/Coupang';

interface ServerFiltersProps {
  filters: MatchedProductsFilters;
  availableCategories: string[];
  availableSubcategories: string[];
  availableProfiles: string[];
  onFilterChange: (key: keyof MatchedProductsFilters, value: string) => void;
  categoriesLoading?: boolean;
  isExporting?: boolean;
  hasRunningExportJob?: boolean;
  onExport?: (subcategories: string[]) => Promise<void>;
  onLoadData?: (subcategories: string[]) => Promise<void>;
  onClearFilters?: () => void;
}

const ServerFilters: React.FC<ServerFiltersProps> = ({
  filters,
  availableCategories,
  availableSubcategories,
  availableProfiles,
  onFilterChange,
  categoriesLoading = false,
  isExporting = false,
  hasRunningExportJob = false,
  onExport,
  onLoadData,
  onClearFilters,
}) => {
  const navigate = useNavigate();

  // Load selected subcategories from sessionStorage on mount
  const [selectedSubcategories, setSelectedSubcategories] = useState<string[]>(() => {
    try {
      const saved = sessionStorage.getItem('matchedProducts.selectedSubcategories');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Track if this is the initial mount
  const previousCategoryRef = useRef<string | undefined>(undefined);

  // Save selected subcategories to sessionStorage whenever they change
  useEffect(() => {
    sessionStorage.setItem('matchedProducts.selectedSubcategories', JSON.stringify(selectedSubcategories));
  }, [selectedSubcategories]);

  // Reset selected subcategories when category actually changes (not on initial mount)
  useEffect(() => {
    // Skip reset on initial mount
    if (previousCategoryRef.current === undefined) {
      previousCategoryRef.current = filters.category;
      return;
    }

    // Only reset if category actually changed
    if (previousCategoryRef.current !== filters.category) {
      setSelectedSubcategories([]);
      previousCategoryRef.current = filters.category;
    }
  }, [filters.category]);

  // Handle category change with navigation
  const handleCategoryChange = (category: string) => {
    onFilterChange('category', category);
    if (category) {
      navigate(ROUTES.sourcing.matchedWithCategory(category));
    } else {
      navigate(ROUTES.sourcing.matched);
    }
  };

  // Handle subcategory selection toggle
  const handleSubcategoryToggle = (subcategory: string) => {
    setSelectedSubcategories((prev) => {
      if (prev.includes(subcategory)) {
        return prev.filter((s) => s !== subcategory);
      } else {
        return [...prev, subcategory];
      }
    });
  };

  // Handle select all subcategories
  const handleSelectAll = () => {
    setSelectedSubcategories([...availableSubcategories]);
  };

  // Handle clear all subcategories
  const handleClearAll = () => {
    setSelectedSubcategories([]);
  };

  // Handle load data button click
  const handleLoadData = async () => {
    if (!filters.category) {
      alert('Please select a category first');
      return;
    }
    if (onLoadData) {
      await onLoadData(selectedSubcategories);
    }
  };

  // Handle export button click
  const handleExport = async () => {
    if (!filters.category) {
      alert('Please select a category first');
      return;
    }
    if (onExport) {
      await onExport(selectedSubcategories);
    }
  };

  const clientFilters = {
    profile: filters.profile,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    scrapingStatus: filters.scrapingStatus,
    outOfStock: filters.outOfStock,
    thirdPartySeller: filters.thirdPartySeller,
  };

  const hasActiveFilters = Object.values(clientFilters).some((value) => value !== '' && value !== 'all');

  return (
    <Row className="mb-2 g-3">
      {/* Select Category Card */}
      <Col md={6}>
        <Card className="border-primary h-100">
          <Card.Header className="bg-primary text-white py-2">
            <h6 className="mb-0">Select Category</h6>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              {/* Category Filter */}
              <Col md={5}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                    Category <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    disabled={categoriesLoading}
                  >
                    <option value="">{categoriesLoading ? 'Loading categories...' : 'Select Category'}</option>
                    {availableCategories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </Form.Select>
                  {categoriesLoading && (
                    <div className="mt-1">
                      <Spinner animation="border" size="sm" className="me-1" />
                      <small className="text-muted">Loading categories...</small>
                    </div>
                  )}
                </Form.Group>
              </Col>

              {/* Subcategory Filter - Multiple Selection */}
              <Col md={5}>
                <Form.Group>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Form.Label className="fw-semibold mb-0" style={{ fontSize: '0.875rem' }}>
                      Subcategory
                    </Form.Label>

                    {filters.category && availableSubcategories.length > 0 && (
                      <div className="d-flex gap-1">
                        <div className="me-2">
                          <small className="text-muted">Selected: {selectedSubcategories.length}</small>
                        </div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleSelectAll}
                          style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}
                        >
                          All
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={handleClearAll}
                          disabled={selectedSubcategories.length === 0}
                          style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}
                        >
                          Clear
                        </Button>
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      maxHeight: '100px',
                      overflowY: 'auto',
                      border: '1px solid #dee2e6',
                      borderRadius: '0.25rem',
                      padding: '0.375rem',
                      backgroundColor: !filters.category || availableSubcategories.length === 0 ? '#e9ecef' : 'white',
                    }}
                  >
                    {!filters.category ? (
                      <small className="text-muted">Select Category First</small>
                    ) : availableSubcategories.length === 0 ? (
                      <small className="text-muted">No subcategories available</small>
                    ) : (
                      availableSubcategories.map((subcategory) => (
                        <Form.Check
                          key={subcategory}
                          type="checkbox"
                          id={`subcategory-${subcategory}`}
                          label={subcategory}
                          checked={selectedSubcategories.includes(subcategory)}
                          onChange={() => handleSubcategoryToggle(subcategory)}
                          style={{ fontSize: '0.8rem' }}
                        />
                      ))
                    )}
                  </div>
                </Form.Group>
              </Col>

              {/* Action Buttons */}
              <Col md={2}>
                <div>
                  <small className="text-muted d-block mb-2" style={{ fontSize: '0.75rem' }}>
                    <a
                      href="https://docs.google.com/spreadsheets/d/1cH8ceSlOgKoaZc_M7UP2iXZ4H6lWcZvfADj6MQgx_LI/edit?gid=0#gid=0"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary"
                    >
                      View Google Sheet →
                    </a>
                  </small>
                  <div className="d-flex flex-column gap-2">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleExport}
                      disabled={!filters.category || isExporting || hasRunningExportJob}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                    >
                      {isExporting ? 'Exporting...' : 'Export to Sheet'}
                    </Button>

                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleLoadData}
                      disabled={!filters.category}
                      style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                    >
                      Load Data
                    </Button>
                  </div>
                  {hasRunningExportJob && (
                    <div className="d-flex align-items-center mt-2">
                      <Spinner animation="border" size="sm" className="me-2" />
                      <small className="text-warning">Exporting...</small>
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>

      {/* Filters Card */}
      <Col md={6}>
        <Card className="border-primary h-100">
          <Card.Header className="bg-primary text-white py-2">
            <div className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Filters</h6>
              {hasActiveFilters && onClearFilters && (
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={onClearFilters}
                  className="d-flex align-items-center"
                  style={{ fontSize: '0.8rem' }}
                >
                  <X size={14} className="me-1" />
                  Clear All
                </Button>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              {/* Profile Filter */}
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                    Profile
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.profile}
                    onChange={(e) => onFilterChange('profile', e.target.value)}
                  >
                    <option value="">All Profiles</option>
                    {availableProfiles.map((profile) => (
                      <option key={profile} value={profile}>
                        {profile}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* From Date Filter */}
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                    From Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={filters.dateFrom}
                    onChange={(e) => onFilterChange('dateFrom', e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* To Date Filter */}
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                    To Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    size="sm"
                    value={filters.dateTo}
                    onChange={(e) => onFilterChange('dateTo', e.target.value)}
                  />
                </Form.Group>
              </Col>

              {/* Scraping Status Filter */}
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                    Scraping Status
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.scrapingStatus}
                    onChange={(e) => onFilterChange('scrapingStatus', e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value={ScrapingStatus.PENDING}>Pending</option>
                    <option value={ScrapingStatus.IN_PROGRESS}>In Progress</option>
                    <option value={ScrapingStatus.COMPLETED}>Completed</option>
                    <option value={ScrapingStatus.FAILED}>Failed</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Stock Status Filter */}
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                    Stock Status
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.outOfStock.toString()}
                    onChange={(e) => onFilterChange('outOfStock', e.target.value)}
                  >
                    <option value="all">All Stock Status</option>
                    <option value="inStock">In Stock</option>
                    <option value="outOfStock">Out of Stock</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {/* Seller Type Filter */}
              <Col md={6} lg={4}>
                <Form.Group>
                  <Form.Label className="fw-semibold" style={{ fontSize: '0.875rem' }}>
                    Seller Type
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.thirdPartySeller.toString()}
                    onChange={(e) => onFilterChange('thirdPartySeller', e.target.value)}
                  >
                    <option value="all">All Sellers</option>
                    <option value="brand">Brand Sellers</option>
                    <option value="thirdParty">3rd Party Sellers</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default ServerFilters;
